import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { Toaster } from './components/ui/sonner';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { Loader2 } from 'lucide-react';
import { notificationAPI, setupAPI } from './lib/api';

// Public Pages
import LandingPage from './pages/public/LandingPage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import TermsOfServicePage from './pages/public/TermsOfServicePage';
import CookiePolicyPage from './pages/public/CookiePolicyPage';

// Cookie Consent Banner
import CookieConsentBanner from './components/common/CookieConsentBanner';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import RegisterChoicePage from './pages/auth/RegisterChoicePage';
import RegisterLandlordPage from './pages/auth/RegisterLandlordPage';
import RegisterTenantPage from './pages/auth/RegisterTenantPage';
import RegisterInvitedTenantPage from './pages/auth/RegisterInvitedTenantPage';
import RegisterManagerPage from './pages/auth/RegisterManagerPage';
import GeneralFormPage from './pages/public/GeneralFormPage';
import PublicFormPage from './pages/public/PublicFormPage';
import SetupPage from './pages/auth/SetupPage';
import AuthCallback from './pages/auth/AuthCallback';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ConfirmEmailPage from './pages/auth/ConfirmEmailPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminLandlords from './pages/admin/AdminLandlords';
import AdminSubscriptions from './pages/admin/AdminSubscriptions';
import AdminSettings from './pages/admin/AdminSettings';
import AdminSupport from './pages/admin/AdminSupport';
import AdminNotifications from './pages/admin/AdminNotifications';

// Landlord Pages
import LandlordDashboard from './pages/landlord/LandlordDashboard';
import LandlordProperties from './pages/landlord/LandlordProperties';
import LandlordUnits from './pages/landlord/LandlordUnits';
import LandlordLeases from './pages/landlord/LandlordLeases';
import LandlordTenants from './pages/landlord/LandlordTenants';
import LandlordPayments from './pages/landlord/LandlordPayments';
import LandlordExpenses from './pages/landlord/LandlordExpenses';
import LandlordBookkeeping from './pages/landlord/LandlordBookkeeping';
import LandlordReports from './pages/landlord/LandlordReports';
import LandlordSettings from './pages/landlord/LandlordSettings';
import LandlordSupport from './pages/landlord/LandlordSupport';
import LandlordRatings from './pages/landlord/LandlordRatings';
import LandlordPropertyManagers from './pages/landlord/LandlordPropertyManagers';
// LandlordAnalytics merged into LandlordReports
import LandlordSubscription from './pages/landlord/LandlordSubscription';
import LandlordLeaseTerms from './pages/landlord/LandlordLeaseTerms';

// Manager Pages
import ManagerDashboard from './pages/manager/ManagerDashboard';
import ManagerProperties from './pages/manager/ManagerProperties';
import ManagerUnits from './pages/manager/ManagerUnits';
import ManagerLeases from './pages/manager/ManagerLeases';
import ManagerAnalytics from './pages/manager/ManagerAnalytics';
import ManagerExpenses from './pages/manager/ManagerExpenses';
import ManagerReports from './pages/manager/ManagerReports';

// Tenant Pages
import TenantDashboard from './pages/tenant/TenantDashboard';
import TenantLease from './pages/tenant/TenantLease';
import TenantPayments from './pages/tenant/TenantPayments';
import TenantReceipts from './pages/tenant/TenantReceipts';
import TenantDocuments from './pages/tenant/TenantDocuments';

// Shared Pages
import FAQsPage from './pages/shared/FAQsPage';
import SupportPage from './pages/shared/SupportPage';

// Shared Pages
import ProfilePage from './pages/shared/ProfilePage';

// Shared Pages
import MessagesPage from './pages/shared/MessagesPage';

import './App.css';

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C85A42]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'landlord') return <Navigate to="/landlord" replace />;
    if (user?.role === 'property_manager') return <Navigate to="/manager" replace />;
    return <Navigate to="/tenant" replace />;
  }

  return children;
}

// Public Route (redirect if authenticated)
function PublicRoute({ children }) {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C85A42]" />
      </div>
    );
  }

  // Allow registration pages with a code param to load even when logged in
  // so landlords can test/preview the links they share
  const params = new URLSearchParams(location.search);
  if (params.get('code') && location.pathname.startsWith('/register')) {
    return children;
  }

  if (isAuthenticated) {
    // Check if there's a redirect destination in state (from ProtectedRoute)
    const from = location.state?.from?.pathname;
    if (from && from !== '/login' && from !== '/register' && from !== '/') {
      return <Navigate to={from} replace />;
    }
    // Otherwise redirect to role-based dashboard
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'landlord') return <Navigate to="/landlord" replace />;
    if (user?.role === 'property_manager') return <Navigate to="/manager" replace />;
    return <Navigate to="/tenant" replace />;
  }

  return children;
}

// Settings placeholder page
function SettingsPage() {
  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="font-heading text-2xl lg:text-3xl font-bold">Settings</h1>
      <p className="text-muted-foreground">Settings page coming soon.</p>
    </div>
  );
}

// Notifications placeholder page
function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await notificationAPI.getNotifications();
      setNotifications(response.data);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.notification_id === id ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationAPI.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.notification_id !== id));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'rent_reminder': return '🏠';
      case 'late_payment': return '⚠️';
      case 'lease_reminder': return '📋';
      default: return '🔔';
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C85A42]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Notifications</h1>
          <p className="text-muted-foreground mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notification(s)` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="text-sm text-[#C85A42] hover:underline"
          >
            Mark all as read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="bg-card rounded-lg border p-12 text-center">
          <div className="text-4xl mb-4">🔔</div>
          <h3 className="font-semibold text-lg mb-2">No notifications yet</h3>
          <p className="text-muted-foreground">
            You'll receive notifications about rent reminders, payments, and lease updates here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <div
              key={notification.notification_id}
              className={`bg-card rounded-lg border p-4 transition-all ${
                !notification.read ? 'border-l-4 border-l-[#C85A42] bg-[#C85A42]/5 dark:bg-[#C85A42]/10' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                <span className="text-2xl">{getNotificationIcon(notification.type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{notification.title}</h3>
                    {!notification.read && (
                      <span className="w-2 h-2 rounded-full bg-[#C85A42]"></span>
                    )}
                  </div>
                  <p className="text-muted-foreground text-sm">{notification.message}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(notification.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!notification.read && (
                    <button
                      onClick={() => handleMarkAsRead(notification.notification_id)}
                      className="text-xs text-[#6B8E6B] hover:underline"
                    >
                      Mark read
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(notification.notification_id)}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Router Component
function AppRouter() {
  const location = useLocation();
  const { setupRequired, setupLoading } = useSetup();

  // Check for session_id in URL (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Show loading while checking setup status
  if (setupLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F5F0E8] to-[#E8E0D5] dark:from-gray-900 dark:to-gray-800">
        <Loader2 className="w-8 h-8 animate-spin text-[#C85A42]" />
      </div>
    );
  }

  // If setup is required, only show setup page
  if (setupRequired) {
    return (
      <Routes>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Setup Route - redirects to login if setup complete */}
      <Route path="/setup" element={<Navigate to="/login" replace />} />

      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterChoicePage /></PublicRoute>} />
      <Route path="/register/landlord" element={<PublicRoute><RegisterLandlordPage /></PublicRoute>} />
      <Route path="/register/tenant" element={<PublicRoute><RegisterTenantPage /></PublicRoute>} />
      <Route path="/register/tenant/invite" element={<PublicRoute><RegisterInvitedTenantPage /></PublicRoute>} />
      <Route path="/register/manager" element={<PublicRoute><RegisterManagerPage /></PublicRoute>} />
      <Route path="/register/old" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="/confirm-email" element={<ConfirmEmailPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forms/general/:code" element={<GeneralFormPage />} />
      <Route path="/forms/:token" element={<PublicFormPage />} />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="landlords" element={<AdminLandlords />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="support" element={<AdminSupport />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="faqs" element={<FAQsPage />} />
      </Route>

      {/* Landlord Routes */}
      <Route
        path="/landlord"
        element={
          <ProtectedRoute allowedRoles={['landlord']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<LandlordDashboard />} />
        <Route path="properties" element={<LandlordProperties />} />
        <Route path="units" element={<LandlordUnits />} />
        <Route path="leases" element={<LandlordLeases />} />
        <Route path="tenants" element={<LandlordTenants />} />
        <Route path="payments" element={<LandlordPayments />} />
        <Route path="expenses" element={<LandlordExpenses />} />
        <Route path="bookkeeping" element={<LandlordBookkeeping />} />
        <Route path="analytics" element={<Navigate to="/landlord/reports" replace />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="reports" element={<LandlordReports />} />
        <Route path="subscription" element={<LandlordSubscription />} />
        <Route path="settings" element={<LandlordSettings />} />
        <Route path="lease-terms" element={<LandlordLeaseTerms />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="support" element={<LandlordSupport />} />
        <Route path="ratings" element={<LandlordRatings />} />
        <Route path="managers" element={<LandlordPropertyManagers />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="faqs" element={<FAQsPage />} />
      </Route>

      {/* Property Manager Routes */}
      <Route
        path="/manager"
        element={
          <ProtectedRoute allowedRoles={['property_manager']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ManagerDashboard />} />
        <Route path="properties" element={<ManagerProperties />} />
        <Route path="units" element={<ManagerUnits />} />
        <Route path="leases" element={<ManagerLeases />} />
        <Route path="tenants" element={<LandlordTenants />} />
        <Route path="payments" element={<LandlordPayments />} />
        <Route path="expenses" element={<ManagerExpenses />} />
        <Route path="analytics" element={<Navigate to="/manager/reports" replace />} />
        <Route path="reports" element={<ManagerReports />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="faqs" element={<FAQsPage />} />
      </Route>

      {/* Tenant Routes */}
      <Route
        path="/tenant"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<TenantDashboard />} />
        <Route path="lease" element={<TenantLease />} />
        <Route path="payments" element={<TenantPayments />} />
        <Route path="receipts" element={<TenantReceipts />} />
        <Route path="documents" element={<TenantDocuments />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="faqs" element={<FAQsPage />} />
      </Route>

      {/* Legal Pages (Public) */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />

      {/* Public Landing Page */}
      <Route path="/home" element={<LandingPage />} />
      <Route path="/landing" element={<LandingPage />} />
      {/* Section deep-links — render LandingPage and auto-scroll to the anchor */}
      <Route path="/pricing" element={<LandingPage />} />
      <Route path="/features" element={<LandingPage />} />
      <Route path="/testimonials" element={<LandingPage />} />
      <Route path="/contact" element={<LandingPage />} />

      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

import { ThemeProvider } from './contexts/ThemeContext';

// Setup Context - checks if first-time setup is required
const SetupContext = createContext();

export function useSetup() {
  return useContext(SetupContext);
}

function SetupProvider({ children }) {
  const [setupRequired, setSetupRequired] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await setupAPI.getStatus();
      setSetupRequired(response.data.setup_required);
    } catch (error) {
      console.error('Failed to check setup status:', error);
      // If we can't check, assume setup is complete
      setSetupRequired(false);
    } finally {
      setSetupLoading(false);
    }
  };

  const completeSetup = () => {
    setSetupRequired(false);
  };

  return (
    <SetupContext.Provider value={{ setupRequired, setupLoading, completeSetup }}>
      {children}
    </SetupContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <SetupProvider>
            <AuthProvider>
              <SubscriptionProvider>
                <AppRouter />
                <CookieConsentBanner />
                <Toaster position="top-right" richColors />
              </SubscriptionProvider>
            </AuthProvider>
          </SetupProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
